export class PolygonConverterUtil {
  /**
   * Convert polygonJson string to coordinate array
   * Handles various formats: JSON string, JSON array, coordinate objects, or WKT format
   */
  static polygonJsonToCoordinates(polygonJson: string | null | undefined): [number, number][] {
    if (!polygonJson) {
      return [];
    }

    try {
      // Check if it's WKT format (starts with POLYGON, MULTIPOLYGON, etc.)
      if (typeof polygonJson === 'string' && polygonJson.trim().toUpperCase().startsWith('POLYGON')) {
        return this.parseWKTPolygon(polygonJson);
      }

      // Parse the JSON string
      const parsed = typeof polygonJson === 'string' ? JSON.parse(polygonJson) : polygonJson;

      // Handle array of coordinates
      if (Array.isArray(parsed)) {
        return this.normalizeCoordinateArray(parsed);
      }

      // Handle GeoJSON format
      if (parsed.type === 'Polygon' && parsed.coordinates) {
        return this.normalizeCoordinateArray(parsed.coordinates[0]);
      }

      // Handle single coordinate object
      if (parsed.lat !== undefined && parsed.lng !== undefined) {
        return [[parsed.lat, parsed.lng]];
      }

      console.warn('Unknown polygonJson format:', parsed);
      return [];
    } catch (error) {
      console.error('Error parsing polygonJson:', error, polygonJson);
      return [];
    }
  }

  /**
   * Parse WKT (Well-Known Text) POLYGON format
   * Example: "POLYGON ((lng lat, lng lat, ...))"
   */
  private static parseWKTPolygon(wkt: string): [number, number][] {
    try {
      // Remove "POLYGON " prefix and trim
      let coords = wkt.trim().replace(/^POLYGON\s*/i, '');

      // Extract the outer ring (first set of parentheses)
      // WKT format can have multiple rings: ((outer ring), (hole1), (hole2))
      const outerRingMatch = coords.match(/^\(\(([^)]+)\)/);

      if (!outerRingMatch) {
        console.warn('Could not parse WKT polygon:', wkt);
        return [];
      }

      const coordString = outerRingMatch[1];

      // Split by comma to get individual coordinate pairs
      const coordPairs = coordString.split(',').map(pair => pair.trim());

      return coordPairs.map(pair => {
        const [lngStr, latStr] = pair.split(/\s+/);
        const lng = parseFloat(lngStr);
        const lat = parseFloat(latStr);

        if (isNaN(lng) || isNaN(lat)) {
          console.warn('Invalid coordinate pair:', pair);
          return [0, 0] as [number, number];
        }

        // WKT uses lng, lat order - return as [lat, lng] for map compatibility
        return [lat, lng] as [number, number];
      }).filter(([lat, lng]) => {
        // Filter out invalid coordinates
        return isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
      });
    } catch (error) {
      console.error('Error parsing WKT polygon:', error, wkt);
      return [];
    }
  }

  /**
   * Normalize coordinate array to [lat, lng] format
   */
  private static normalizeCoordinateArray(coords: any[]): [number, number][] {
    return coords.map(coord => {
      // Handle [lat, lng] or [lng, lat] array format
      if (Array.isArray(coord) && coord.length >= 2) {
        const val0 = Number(coord[0]);
        const val1 = Number(coord[1]);

        // Detect if coordinates are swapped (longitude first)
        if (Math.abs(val0) > 90 && Math.abs(val1) <= 90) {
          return [val1, val0] as [number, number]; // Swap to [lat, lng]
        }
        return [val0, val1] as [number, number];
      }

      // Handle object format {lat, lng} or {latitude, longitude}
      if (typeof coord === 'object') {
        const lat = Number(coord.lat || coord.latitude || 0);
        const lng = Number(coord.lng || coord.lon || coord.longitude || 0);
        return [lat, lng] as [number, number];
      }

      console.warn('Invalid coordinate format:', coord);
      return [0, 0] as [number, number];
    }).filter(coord => {
      // Filter out invalid coordinates
      const [lat, lng] = coord;
      return isFinite(lat) && isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
    });
  }

  /**
   * Calculate center (centroid) of polygon using the area-weighted method
   * Supports both flat coordinates and multi-coordinates structure
   * @param coordinates - Either [number, number][] (flat) or [number, number][][][] (multi)
   */
  static getPolygonCenter(coordinates: [number, number][] | [number, number][][][]): { lat: number; lng: number } | null {
    if (!coordinates || coordinates.length === 0) {
      return null;
    }

    // Check if it's multi-coordinates structure [number, number][][][]
    if (this.isMultiCoordinates(coordinates)) {
      return this.getPolygonCenterFromMulti(coordinates as [number, number][][][]);
    }

    // Handle flat coordinates [number, number][]
    return this.getPolygonCenterFromFlat(coordinates as [number, number][]);
  }

  /**
   * Check if coordinates are in multi-coordinates format [number, number][][][]
   */
  private static isMultiCoordinates(coordinates: any): coordinates is [number, number][][][] {
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return false;
    }
    // Check first element - if it's an array of arrays of arrays, it's multi-coordinates
    const first = coordinates[0];
    if (!Array.isArray(first) || first.length === 0) {
      return false;
    }
    const second = first[0];
    if (!Array.isArray(second) || second.length === 0) {
      return false;
    }
    // Check if the deepest level is a coordinate pair [number, number]
    const third = second[0];
    return Array.isArray(third) && third.length === 2 && typeof third[0] === 'number';
  }


  static getPolygonCenterFromMulti(multiCoordinates: [number, number][][][]): { lat: number; lng: number } | null {
    if (!multiCoordinates || multiCoordinates.length === 0) {
      return null;
    }

    let south = Infinity;
    let north = -Infinity;
    let west = Infinity;
    let east = -Infinity;

    // Find bounding box across all polygons (outer rings only)
    for (const polygon of multiCoordinates) {
      if (polygon.length > 0) {
        const outerRing = polygon[0]; // Use outer ring only
        for (const [lat, lng] of outerRing) {
          if (lat < south) south = lat;
          if (lat > north) north = lat;
          if (lng < west) west = lng;
          if (lng > east) east = lng;
        }
      }
    }

    // Check if we found valid bounds
    if (!isFinite(south) || !isFinite(north) || !isFinite(west) || !isFinite(east)) {
      // Fallback: use the center of the first polygon's outer ring
      if (multiCoordinates[0]?.length > 0) {
        return this.getPolygonCenterFromFlat(multiCoordinates[0][0]);
      }
      return null;
    }

    return {
      lat: (south + north) / 2,
      lng: (west + east) / 2
    };
  }

  /**
   * Calculate center (centroid) of flat polygon coordinates using the area-weighted method
   */
  static getPolygonCenterFromFlat(coordinates: [number, number][]): { lat: number; lng: number } | null {
    if (!coordinates || coordinates.length === 0) {
      return null;
    }
    if (coordinates.length < 3) {
      // For lines or points, fallback to average
      let sumLat = 0;
      let sumLng = 0;
      coordinates.forEach(([lat, lng]) => {
        sumLat += lat;
        sumLng += lng;
      });
      return {
        lat: sumLat / coordinates.length,
        lng: sumLng / coordinates.length
      };
    }

    let area = 0;
    let cx = 0;
    let cy = 0;
    const n = coordinates.length;
    for (let i = 0; i < n; i++) {
      const [lat0, lng0] = coordinates[i];
      const [lat1, lng1] = coordinates[(i + 1) % n];
      const factor = (lng0 * lat1 - lng1 * lat0);
      area += factor;
      cx += (lat0 + lat1) * factor;
      cy += (lng0 + lng1) * factor;
    }
    area *= 0.5;
    if (area === 0) {
      // Degenerate polygon, fallback to average
      let sumLat = 0;
      let sumLng = 0;
      coordinates.forEach(([lat, lng]) => {
        sumLat += lat;
        sumLng += lng;
      });
      return {
        lat: sumLat / coordinates.length,
        lng: sumLng / coordinates.length
      };
    }
    cx /= (6 * area);
    cy /= (6 * area);
    return { lat: cx, lng: cy };
  }

  /**
   * Validate polygon has minimum required coordinates
   */
  static isValidPolygon(coordinates: [number, number][]): boolean {
    return coordinates && coordinates.length >= 3;
  }



  /**
   * Calculate total area from multi-coordinates structure [number, number][][][]
   * For polygons with holes, subtracts hole areas from outer ring area
   */
  static calculatePolygonAreaFromMulti(multiCoordinates: [number, number][][][]): number {
    if (!multiCoordinates || multiCoordinates.length === 0) {
      return 0;
    }

    let totalArea = 0;

    for (const polygon of multiCoordinates) {
      if (polygon.length > 0) {
        // First ring is the outer ring
        const outerArea = this.calculatePolygonAreaFromFlat(polygon[0]);

        // Subsequent rings are holes - subtract their area
        let holesArea = 0;
        for (let i = 1; i < polygon.length; i++) {
          holesArea += this.calculatePolygonAreaFromFlat(polygon[i]);
        }

        totalArea += outerArea - holesArea;
      }
    }

    return Math.abs(totalArea);
  }

  /**
   * Calculate polygon area from flat coordinates (approximate, in square degrees)
   */
  static calculatePolygonAreaFromFlat(coordinates: [number, number][]): number {
    if (!this.isValidPolygon(coordinates)) {
      return 0;
    }

    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const [lat1, lng1] = coordinates[i];
      const [lat2, lng2] = coordinates[j];
      area += lng1 * lat2 - lng2 * lat1;
    }

    return Math.abs(area / 2);
  }

  /**
   * Convert array of {lon, lat} objects to WKT POLYGON format string
   * Input: [{lon: 63.31, lat: 42.26}, {lon: 63.54, lat: 42.41}, ...]
   * Output: "POLYGON ((63.31 42.26, 63.54 42.41, ...))"
   */
  static coordinatesLatLonToWKT(coordinates: Array<{lon: number, lat: number}> | Array<{lng: number, lat: number}> | [number, number][]): string {
    if (!coordinates || coordinates.length === 0) {
      return '';
    }

    const coordStrings: string[] = coordinates.map(coord => {
      // Handle {lon, lat} format
      if ('lon' in coord && 'lat' in coord) {
        return `${coord.lon} ${coord.lat}`;
      }
      // Handle {lng, lat} format
      if ('lng' in coord && 'lat' in coord) {
        return `${coord.lng} ${coord.lat}`;
      }
      // Handle [lat, lng] array format - need to swap to [lng, lat] for WKT
      if (Array.isArray(coord) && coord.length >= 2) {
        return `${coord[1]} ${coord[0]}`;
      }
      return '';
    }).filter(s => s !== '');

    if (coordStrings.length === 0) {
      return '';
    }

    // Ensure the polygon is closed (first point = last point)
    const firstCoord = coordStrings[0];
    const lastCoord = coordStrings[coordStrings.length - 1];
    if (firstCoord !== lastCoord) {
      coordStrings.push(firstCoord);
    }

    return `POLYGON ((${coordStrings.join(', ')}))`;
  }

  /**
   * Normalize WKT polygons that come with comma-separated coordinate pairs ("lon,lat lon,lat")
   * into standard WKT ("lon lat, lon lat").
   */
  static normalizePolygonWkt(polygon: string): string {
    if (!polygon || typeof polygon !== 'string') {
      return '';
    }

    const match = polygon.trim().match(/POLYGON\s*\(\(\s*(.+)\s*\)\)/i);
    if (!match || !match[1]) {
      return polygon;
    }

    const coordsPart = match[1];
    const tokens = coordsPart.trim().split(/\s+/);

    const isCommaSeparated = tokens.every(tok => tok.includes(','));
    if (!isCommaSeparated) {
      return polygon;
    }

    const coords: [number, number][] = [];
    tokens.forEach(tok => {
      const [lonStr, latStr] = tok.split(',');
      const lon = parseFloat(lonStr);
      const lat = parseFloat(latStr);
      if (!isNaN(lon) && !isNaN(lat)) {
        coords.push([lat, lon]);
      }
    });

    if (coords.length < 3) {
      return polygon;
    }

    return PolygonConverterUtil.coordinatesLatLonToWKT(coords);
  }


  static wktToCoordinatesLatLon(wkt: string): Array<{lon: number, lat: number}> {
    if (!wkt || typeof wkt !== 'string' || !wkt.trim().toUpperCase().startsWith('POLYGON')) {
      return [];
    }
    // Extract coordinates from WKT
    const match = wkt.match(/^\s*POLYGON\s*\(\(\s*([^)]+)\s*\)\)/i);
    if (!match) {
      return [];
    }
    const coordPairs = match[1].split(',').map(pair => pair.trim());
    // WKT is "lng lat"
    return coordPairs.map(pair => {
      const [lngStr, latStr] = pair.split(/\s+/);
      return {
        lon: parseFloat(lngStr),
        lat: parseFloat(latStr)
      };
    }).filter(coord => !isNaN(coord.lon) && !isNaN(coord.lat));
  }




  /**
   * Convert [lat, lng][] array to WKT POLYGON format string
   * Input: [[42.26, 63.31], [42.41, 63.54], ...]
   * Output: "POLYGON ((63.31 42.26, 63.54 42.41, ...))"
   */
  static latLngArrayToWKT(coordinates: [number, number][]): string {
    if (!coordinates || coordinates.length === 0) {
      return '';
    }

    const coordStrings: string[] = coordinates.map(([lat, lng]) => `${lng} ${lat}`);

    // Ensure the polygon is closed (first point = last point)
    const firstCoord = coordStrings[0];
    const lastCoord = coordStrings[coordStrings.length - 1];
    if (firstCoord !== lastCoord) {
      coordStrings.push(firstCoord);
    }

    return `POLYGON ((${coordStrings.join(', ')}))`;
  }


  /**
   * Calculate polygon area (approximate, in square degrees)
   */
  static calculatePolygonArea(coordinates: [number, number][]): number {
    if (!this.isValidPolygon(coordinates)) {
      return 0;
    }

    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const [lat1, lng1] = coordinates[i];
      const [lat2, lng2] = coordinates[j];
      area += lng1 * lat2 - lng2 * lat1;
    }

    return Math.abs(area / 2);
  }

  /**
   * Calculate polygon area in hectares
   * Converts from square degrees to hectares using geographic conversion
   * @param coordinates Array of [lat, lng] coordinates
   * @returns Area in hectares formatted to 2 decimal places, or null if invalid
   */
  static calculatePolygonAreaInHectares(coordinates: [number, number][]): string | null {
    if (!this.isValidPolygon(coordinates)) {
      return null;
    }

    try {
      // Calculate area in square degrees
      const areaDegrees = this.calculatePolygonArea(coordinates);

      // Convert square degrees to hectares (approximate)
      // 1 degree latitude ≈ 111 km, 1 degree longitude varies by latitude
      // Using average latitude for rough conversion
      const avgLat = coordinates.reduce((sum, coord) => sum + coord[0], 0) / coordinates.length;
      const latFactor = 111.32; // km per degree latitude
      const lngFactor = 111.32 * Math.cos(avgLat * Math.PI / 180); // km per degree longitude

      // Area in square kilometers
      const areaKm2 = areaDegrees * latFactor * lngFactor;

      // Convert to hectares (1 km² = 100 hectares)
      const areaHectares = areaKm2 * 100;

      // Format with 2 decimal places
      return areaHectares.toFixed(2);
    } catch (error) {
      console.error('Error calculating polygon area in hectares:', error);
      return null;
    }
  }

  /**
   * Convertit un GeoJSON Polygon (ou MultiPolygon) en tableau de coordonnées [lat, lon] pour Leaflet
   * Inverse la position lon/lat -> lat/lon
   */
  static polygonJsonToLatLng(polygonJson: any): [number, number][] {
    if (!polygonJson || !polygonJson.coordinates) return [];
    // Polygon simple
    if (polygonJson.type === 'Polygon') {
      return polygonJson.coordinates.map((ring: [number, number][]) =>
        ring.map(([lon, lat]) => [lat, lon])
      );
    }
    // MultiPolygon
    if (polygonJson.type === 'MultiPolygon') {
      return polygonJson.coordinates.flat().map((ring: [number, number][]) =>
        ring.map(([lon, lat]) => [lat, lon])
      );
    }
    return [];
  }

  /**
   * Convertit un GeoJSON Polygon (ou MultiPolygon) en tableau plat [lat, lon] pour Leaflet
   * Prend le premier ring uniquement
   */
  static polygonJsonToLatLngFlat(polygonJson: any): [number, number][] {
    if (!polygonJson || !polygonJson.coordinates) return [];
    // Polygon simple
    if (polygonJson.type === 'Polygon') {
      // @ts-ignore
      return polygonJson.coordinates[0].map(([lon, lat]) => [lat, lon]);
    }
    // MultiPolygon
    if (polygonJson.type === 'MultiPolygon') {
      // Prend le premier ring du premier polygon
      // @ts-ignore
      return polygonJson.coordinates[0][0].map(([lon, lat]) => [lat, lon]);
    }
    return [];
  }

}
